import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserLearningPaths } from '../services/firebase';
import './Progress.css';

function Progress() {
    const { currentUser, userData } = useAuth();
    const [learningPaths, setLearningPaths] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadAllProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userData]);

    const loadAllProgress = async () => {
        if (!currentUser || !userData) {
            setLoading(false);
            return;
        }

        try {
            // Load all learning paths from Firebase
            const pathsResult = await getUserLearningPaths(currentUser.uid);
            if (pathsResult.success) {
                setLearningPaths(pathsResult.data);
            }
        } catch (error) {
            console.error('Error loading progress:', error);
        }
        setLoading(false);
    };

    const getLevel = (xp) => {
        if (xp < 500) return { level: 1, title: 'Beginner', icon: '🌱', nextLevel: 500 };
        if (xp < 1000) return { level: 2, title: 'Learner', icon: '📚', nextLevel: 1000 };
        if (xp < 2000) return { level: 3, title: 'Student', icon: '🎓', nextLevel: 2000 };
        if (xp < 5000) return { level: 4, title: 'Scholar', icon: '👨‍🎓', nextLevel: 5000 };
        return { level: 5, title: 'Master', icon: '🏆', nextLevel: null };
    };

    // Use userData from AuthContext (Firebase) as single source of truth
    const totalXP = userData?.totalXP || 0;
    const levelInfo = getLevel(totalXP);

    // Calculate XP progress within current level
    const levelThresholds = [0, 500, 1000, 2000, 5000];
    const currentLevelStart = levelThresholds[levelInfo.level - 1] || 0;
    const nextLevelStart = levelInfo.nextLevel || totalXP;
    const xpInCurrentLevel = totalXP - currentLevelStart;
    const xpNeededForNextLevel = nextLevelStart - currentLevelStart;
    const progressPercent = levelInfo.nextLevel
        ? (xpInCurrentLevel / xpNeededForNextLevel) * 100
        : 100;

    // Calculate stats from userData
    const completedModulesCount = userData?.completedModules?.length || 0;
    const totalQuestions = userData?.stats?.totalQuestions || 0;
    const correctAnswers = userData?.stats?.correctAnswers || 0;
    const accuracy = totalQuestions > 0
        ? Math.round((correctAnswers / totalQuestions) * 100)
        : 0;
    const currentStreak = userData?.streak || 0;

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Loading your progress...</p>
            </div>
        );
    }

    return (
        <div className="progress-container">
            <h1>Your Progress</h1>
            <p className="subtitle">Track your learning journey</p>

            {/* Level Card */}
            <div className="level-card">
                <div className="level-icon">{levelInfo.icon}</div>
                <h2>{levelInfo.title}</h2>
                <p className="level-text">Level {levelInfo.level}</p>
                {levelInfo.nextLevel ? (
                    <p className="xp-text">{xpInCurrentLevel} / {xpNeededForNextLevel} XP to next level</p>
                ) : (
                    <p className="xp-text">Max Level Reached! 🏆</p>
                )}
                <div className="level-progress">
                    <div
                        className="level-progress-fill"
                        style={{
                            width: `${progressPercent}%`,
                            background: 'var(--gradient-primary)'
                        }}
                    />
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card">
                    <span className="stat-value">{totalXP}</span>
                    <span className="stat-label">Total XP</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">{completedModulesCount}</span>
                    <span className="stat-label">Modules Completed</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">{accuracy}%</span>
                    <span className="stat-label">Accuracy</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">{currentStreak} 🔥</span>
                    <span className="stat-label">Current Streak</span>
                </div>
            </div>

            {/* Learning Paths */}
            <div className="topics-section">
                <h2>📚 Your Learning Paths</h2>
                {learningPaths.length === 0 ? (
                    <div className="empty-state">
                        <p>No learning paths yet. Start your first journey!</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/home')}
                        >
                            Start Learning →
                        </button>
                    </div>
                ) : (
                    <div className="topics-grid">
                        {learningPaths.map((path, index) => {
                            // Count completed modules for this path
                            const pathModulesCompleted = userData?.completedModules?.filter(
                                m => m.startsWith(`${path.topic}_`)
                            ).length || 0;
                            const totalModules = path.totalModules || 5;
                            const completionPercent = (pathModulesCompleted / totalModules) * 100;

                            return (
                                <div key={index} className="topic-progress-card">
                                    <h3>{path.topic}</h3>
                                    <div className="topic-progress-bar">
                                        <div
                                            className="topic-progress-fill"
                                            style={{ width: `${completionPercent}%` }}
                                        />
                                    </div>
                                    <div className="topic-stats">
                                        <span>📖 {pathModulesCompleted}/{totalModules} modules</span>
                                        <span>⏱️ {path.estimatedTime || 'N/A'}</span>
                                    </div>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={() => {
                                            navigate('/learning-path', { state: { topic: path.topic } });
                                        }}
                                    >
                                        {completionPercent === 100 ? 'Review' : 'Continue'} →
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Achievements */}
            <div className="achievements-section">
                <h2>🏆 Achievements</h2>
                <div className="achievements-grid">
                    {[
                        {
                            icon: '🌟',
                            name: 'First Steps',
                            desc: 'Complete your first module',
                            unlocked: completedModulesCount > 0
                        },
                        {
                            icon: '🔥',
                            name: 'On Fire',
                            desc: 'Maintain a 5 day streak',
                            unlocked: currentStreak >= 5
                        },
                        {
                            icon: '💯',
                            name: 'Perfect Score',
                            desc: 'Get 100% accuracy',
                            unlocked: accuracy === 100 && totalQuestions >= 10
                        },
                        {
                            icon: '📚',
                            name: 'Knowledge Seeker',
                            desc: 'Start 3 different learning paths',
                            unlocked: learningPaths.length >= 3
                        },
                        {
                            icon: '🎓',
                            name: 'Dedicated Learner',
                            desc: 'Complete 5 learning modules',
                            unlocked: completedModulesCount >= 5
                        },
                        {
                            icon: '💎',
                            name: 'XP Hunter',
                            desc: 'Earn 1000 XP',
                            unlocked: totalXP >= 1000
                        },
                        {
                            icon: '🏆',
                            name: 'Champion',
                            desc: 'Complete an entire learning path',
                            unlocked: learningPaths.some(p => {
                                const completed = userData?.completedModules?.filter(
                                    m => m.startsWith(`${p.topic}_`)
                                ).length || 0;
                                return completed === (p.totalModules || 5);
                            })
                        },
                        {
                            icon: '⚡',
                            name: 'Speed Learner',
                            desc: 'Reach Level 3',
                            unlocked: levelInfo.level >= 3
                        }
                    ].map((achievement, index) => (
                        <div 
                            key={index}
                            className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}
                        >
                            <div className="achievement-icon">{achievement.icon}</div>
                            <h4>{achievement.name}</h4>
                            <p>{achievement.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
                <h2>Continue Learning</h2>
                <div className="action-buttons">
                    <button
                        className="btn btn-primary btn-large"
                        onClick={() => navigate('/home')}
                    >
                        🚀 Start New Topic
                    </button>
                    {learningPaths.length > 0 && (
                        <button
                            className="btn btn-secondary btn-large"
                            onClick={() => {
                                const lastPath = learningPaths[learningPaths.length - 1];
                                navigate('/learning-path', { state: { topic: lastPath.topic } });
                            }}
                        >
                            📚 Continue Last Path
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Progress;